import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { inject, injectable } from 'tsyringe';
import { NotificationList } from './interfaces/Notification.types';
import { AmGraphQlClient } from 'app/cross-cutting-concerns/communication/clients/AmGraphQlClient';
import {
  InputPagingOptions,
  InputSortOptions,
  InputFilterNotificationsList,
  InputFilterMachinesList,
  ResponseNotificationUser,
  ResponseNotificationUpdateNotifyStatus,
  InputNotificationUpdateNotifyStatus,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

export interface NotificationClientListOptions {
  paginationOptions: InputPagingOptions;
  filter?: InputFilterNotificationsList;
  sortOptions?: InputSortOptions;
  lang?: string;
}

export interface NotificationClientMachineListFilterOptions {
  filter: InputFilterMachinesList;
}

export type NotificationClientUpdateNotifyStatusOptions = InputNotificationUpdateNotifyStatus;

@injectable()
export class NotificationClient {
  constructor(@inject('AmGraphQlClient') private client: AmGraphQlClient) {}

  public list = async ({
    paginationOptions,
    filter,
    sortOptions,
    lang,
  }: NotificationClientListOptions): Promise<ApolloQueryResult<NotificationList>> =>
    this.client.query({
      query: gql`
        query Notifications(
          $paginationOptions: InputPagingOptions!
          $filter: InputFilterNotificationsList
          $sortOptions: InputSortOptions
          $lang: String
        ) {
          notifications(
            paginationOptions: $paginationOptions
            filter: $filter
            sortOptions: $sortOptions
            lang: $lang
          ) {
            data {
              id
              timestamp
              description
              type
              severity
              data
              machine {
                id
                name
                materialNumber
                serialNumber
                site {
                  id
                  name
                }
                type {
                  name
                }
                batteryDischargeStatus
              }
              message {
                cause
                description
                consequence
              }
            }
            metadata {
              totalCount
              paginationToken
            }
          }
        }
      `,
      variables: {
        filter: {
          ...(filter?.machineIds && { machineIds: filter?.machineIds }),
          ...(filter?.type && { type: filter?.type }),
          ...(filter?.severities && { severities: filter?.severities }),
          ...(filter?.categories && { categories: filter?.categories }),
          ...(filter?.period && { period: filter?.period }),
        },
        sortOptions,
        paginationOptions,
        lang,
      },
    });

  public listShort = async ({
    paginationOptions,
    filter,
    sortOptions,
    lang,
  }: NotificationClientListOptions): Promise<ApolloQueryResult<NotificationList>> =>
    this.client.query({
      query: gql`
        query Notifications(
          $paginationOptions: InputPagingOptions!
          $filter: InputFilterNotificationsList
          $sortOptions: InputSortOptions
          $lang: String
        ) {
          notifications(
            paginationOptions: $paginationOptions
            filter: $filter
            sortOptions: $sortOptions
            lang: $lang
          ) {
            data {
              id
              timestamp
              description
              type
              severity
              data
              machine {
                id
                name
              }
              message {
                cause
                description
                consequence
              }
            }
            metadata {
              totalCount
              paginationToken
            }
          }
        }
      `,
      variables: {
        filter: {
          ...(filter?.machineIds && { machineIds: filter?.machineIds }),
          ...(filter?.type && { type: filter?.type }),
          ...(filter?.severities && { severities: filter?.severities }),
        },
        sortOptions,
        paginationOptions,
        lang,
      },
    });

  public getUserNotification = async (): Promise<ApolloQueryResult<ResponseNotificationUser>> =>
    this.client.query({
      query: gql`
        query NotificationGetUser {
          notificationGetUser {
            data {
              customerId
              email
              role
              timezone
              assignedMachines {
                id
                name
              }
              notifyLocationStatus
              notifyReminder
              notifyEmergencyStop
              notifyResources
              notifyErrorOccurred
              notifyAutonomousTaskStopped
              notifyCleaningTaskFinished
              notifyNoWorkStart
              notifyOther
              notifyPCM
              notifyBatteryDeepDischarge
              notifyInformation
              notifyServiceNeeded
              notifyCleaningTaskInterrupted
              emailSubscription
            }
          }
        }
      `,
    });

  public updateNotifyStatus = async ({
    assignedMachines,
    emailSubscription,
    lang,
    notifyAutonomousTaskStopped,
    notifyCleaningTaskFinished,
    notifyCleaningTaskInterrupted,
    notifyEmergencyStop,
    notifyErrorOccurred,
    notifyInformation,
    notifyLocationStatus,
    notifyNoWorkStart,
    notifyPCM,
    notifyOther,
    notifyReminder,
    notifyResources,
    notifyServiceNeeded,
    notifyBatteryDeepDischarge,
    timezone,
  }: NotificationClientUpdateNotifyStatusOptions): Promise<FetchResult<ResponseNotificationUpdateNotifyStatus>> =>
    this.client.mutate({
      mutation: gql`
        mutation NotificationUpdateNotifyStatus($input: InputNotificationUpdateNotifyStatus!) {
          notificationUpdateNotifyStatus(input: $input) {
            data {
              notifyAutonomousTaskStopped
              notifyCleaningTaskFinished
              notifyCleaningTaskInterrupted
              notifyEmergencyStop
              notifyErrorOccurred
              notifyInformation
              notifyLocationStatus
              notifyNoWorkStart
              notifyOther
              notifyPCM
              notifyReminder
              notifyResources
              notifyServiceNeeded
              notifyBatteryDeepDischarge
              timezone
            }
          }
        }
      `,
      variables: {
        input: {
          assignedMachines,
          emailSubscription,
          lang,
          notifyAutonomousTaskStopped,
          notifyCleaningTaskFinished,
          notifyCleaningTaskInterrupted,
          notifyEmergencyStop,
          notifyErrorOccurred,
          notifyInformation,
          notifyLocationStatus,
          notifyNoWorkStart,
          notifyPCM,
          notifyOther,
          notifyReminder,
          notifyResources,
          notifyServiceNeeded,
          notifyBatteryDeepDischarge,
          timezone,
        },
      },
    });
}
